import { Localized, useLocalization } from "@fluent/react";
import { useAppDispatch, useAppSelector, useDataGridCustomLocaleText } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import produce from "immer";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Button, ButtonGroup, Paper, TableContainer } from "@mui/material";

import { showSuccess } from "../notifications/notificationsSlice";
import { StructureName } from "../device/Structure";
import { LoadingContainer } from "../../app/LoadingContainer";
import { ErrorContainer } from "../../app/ErrorContainer";
import { FlexDiv } from "../../app/Utils";
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar";
import { AddFabButton } from "../../app/components/AddFabButton";
import { selectSettings } from "../settings/settingsSlice";
import { format } from "date-fns";
import { loadRoomGroups, RoomGroup } from "./roomsApi";
import { CreateRoomGroupDialog } from "./dialogs/CreateRoomGroupDialog";
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';

export const RoomGroupList = () => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const localeText = useDataGridCustomLocaleText();

    const [groups, setGroups] = useState<RoomGroup[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);

    const [openCreate, setOpenCreate] = useState(false);

    const { locale } = useAppSelector(selectSettings);
    const lang = locale === "pl" ? "pl" : "en";

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadRoomGroups())
            .then(unwrapResult)
            .then((data: RoomGroup[]) => setGroups(data))
            .catch((err) => {
                console.error(err);
                setIsError(true);
                setError(err);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch]);

    const handleOpen = () => setOpenCreate(true);
    const handleCancel = () => setOpenCreate(false);

    const handleSuccess = (group: RoomGroup) => {
        setGroups((prev) =>
            produce(prev, (draft) => {
                const idx = draft.findIndex((o) => o.id === group.id);
                if (idx >= 0) {
                    draft[idx] = { ...draft[idx], ...group };
                } else {
                    draft.unshift(group);
                }
            })
        );
        setOpenCreate(false);
        dispatch(showSuccess("saved"));
        navigate(`/room-groups/edit/${group.id}`);
    };

    const handleGoToClick = (id: number) => navigate(`/room-groups/edit/${id}`);

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: l10n.getString("rg-name", null, "Nazwa grupy"),
            flex: 1,
            minWidth: 220,
        },
        {
            field: "description",
            headerName: l10n.getString("rg-description", null, "Opis"),
            flex: 1,
            minWidth: 220,
            sortable: false,
            valueGetter: (_, row) => row.description ?? "",
        },
        {
            field: "structureId",
            headerName: l10n.getString("structure", null, "_structure"),
            flex: 1,
            minWidth: 200,
            renderCell: (params) => <StructureName structureId={params.row.structureId} />,
        },
        {
            field: "createDate",
            width: 150,
            headerName: l10n.getString("process-create-date", null, "Data utworzenia"),
            valueGetter: (_, row) => (row.createDate ? new Date(row.createDate) : null),
            valueFormatter: (value) => (value ? format(value as Date, "dd-MM-yyyy") : ""),
            sortComparator: (v1, v2) => {
                const d1 = v1 ? new Date(v1 as Date).getTime() : 0;
                const d2 = v2 ? new Date(v2 as Date).getTime() : 0;
                return d1 - d2;
            },
        },
        {
            field: "actions",
            type: "actions",
            width: 200,
            getActions: (params) => [
                <ButtonGroup key="actions">
                    <Button onClick={() => handleGoToClick(Number(params.id))} color="secondary" variant="outlined">
                        <Localized id="go-to">
                            <span>Edytuj</span>
                        </Localized>
                    </Button>
                </ButtonGroup>,
            ],
        },
    ];

    if (isLoading) return <LoadingContainer />;
    if (isError) return <ErrorContainer error={error} />;

    return (
        <>
            <TableContainer component={Paper}>
                <div style={{ display: "flex", height: "100%" }}>
                    <div style={{ flexGrow: 1 }}>
                        <DataGrid
                            style={{ minHeight: 100, height: "calc(100vh - 200px)" }}
                            localeText={localeText}
                            rows={groups}
                            columns={columns}
                            getRowId={(r) => r.id}
                            slots={{ toolbar: QuickSearchToolbar }}
                            initialState={{
                                sorting: { sortModel: [{ field: "createDate", sort: "desc" }] },
                            }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                        />
                    </div>
                </div>
                <FlexDiv>
                    <AddFabButton onClick={handleOpen} />
                </FlexDiv>
            </TableContainer>

            {openCreate && <CreateRoomGroupDialog onCancel={handleCancel} onSuccess={handleSuccess} />}
        </>
    );
};
